import type { AudioContextManager } from './AudioContext';

type BorderDirection = 'left' | 'right' | 'top' | 'bottom';

interface MelodyNote {
    freq: number;
    time: number;
}

/**
 * 효과음 생성 담당
 */
export class SoundEffects {
    private ctxManager: AudioContextManager;
    private eChordLowest: number[];
    private eChordLow: number[];
    private eChordMid: number[];
    private eChordHigh: number[];

    constructor(audioContextManager: AudioContextManager) {
        this.ctxManager = audioContextManager;

        // E major chord notes (E, G#, B) - 옥타브별 정리
        this.eChordLowest = [41.20, 51.91, 61.74];    // E1
        this.eChordLow = [82.41, 103.83, 123.47];     // E2
        this.eChordMid = [164.81, 207.65, 246.94];    // E3
        this.eChordHigh = [329.63, 415.30, 493.88];   // E4
    }

    playBulletBoundarySound(border: BorderDirection = 'left'): void {
        const ctx = this.ctxManager.get();
        if (!ctx) return;

        this.ctxManager.resume();

        let frequencyArray: number[];
        let duration: number;
        let volume: number;

        if (border === 'top') {
            frequencyArray = this.eChordHigh;
            duration = 0.4;
            volume = 0.04;
        } else if (border === 'right') {
            frequencyArray = this.eChordMid;
            duration = 0.5;
            volume = 0.06;
        } else if (border === 'left') {
            frequencyArray = this.eChordLow;
            duration = 0.6;
            volume = 0.08;
        } else {
            frequencyArray = this.eChordLowest;
            duration = 0.7;
            volume = 0.10;
        }

        const frequency = frequencyArray[Math.floor(Math.random() * frequencyArray.length)];
        const now = ctx.currentTime;

        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        const filter = ctx.createBiquadFilter();

        filter.type = 'lowpass';
        filter.frequency.value = 2000;
        filter.Q.value = 1;

        oscillator.connect(filter);
        filter.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.frequency.value = frequency;
        oscillator.type = 'triangle';

        gainNode.gain.setValueAtTime(0, now);
        gainNode.gain.linearRampToValueAtTime(volume, now + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(volume * 0.7, now + duration * 0.6);
        gainNode.gain.exponentialRampToValueAtTime(0.001, now + duration);

        oscillator.start(now);
        oscillator.stop(now + duration);

        oscillator.onended = () => {
            oscillator.disconnect();
            filter.disconnect();
            gainNode.disconnect();
        };
    }

    playCollisionMelody(): void {
        const ctx = this.ctxManager.get();
        if (!ctx) return;

        this.ctxManager.resume();
        const now = ctx.currentTime;

        // 1. Noise burst
        const noiseLength = 0.15;
        const noiseBuffer = ctx.createBuffer(1, noiseLength * ctx.sampleRate, ctx.sampleRate);
        const noiseData = noiseBuffer.getChannelData(0);
        for (let i = 0; i < noiseData.length; i++) {
            noiseData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / noiseData.length, 2);
        }

        const noiseSource = ctx.createBufferSource();
        noiseSource.buffer = noiseBuffer;

        const noiseFilter = ctx.createBiquadFilter();
        noiseFilter.type = 'lowpass';
        noiseFilter.frequency.value = 1000;

        const noiseGain = ctx.createGain();
        noiseGain.gain.setValueAtTime(0.4, now);
        noiseGain.gain.exponentialRampToValueAtTime(0.01, now + noiseLength);

        noiseSource.connect(noiseFilter);
        noiseFilter.connect(noiseGain);
        noiseGain.connect(ctx.destination);
        noiseSource.start(now);

        // 2. Low impact thud
        const thudOsc = ctx.createOscillator();
        const thudGain = ctx.createGain();

        thudOsc.connect(thudGain);
        thudGain.connect(ctx.destination);

        thudOsc.type = 'sine';
        thudOsc.frequency.setValueAtTime(150, now);
        thudOsc.frequency.exponentialRampToValueAtTime(30, now + 0.2);

        thudGain.gain.setValueAtTime(0.5, now);
        thudGain.gain.exponentialRampToValueAtTime(0.01, now + 0.25);

        thudOsc.start(now);
        thudOsc.stop(now + 0.25);

        // 3. Falling pitch
        const fallOsc = ctx.createOscillator();
        const fallGain = ctx.createGain();

        fallOsc.connect(fallGain);
        fallGain.connect(ctx.destination);

        fallOsc.type = 'sawtooth';
        fallOsc.frequency.setValueAtTime(400, now);
        fallOsc.frequency.exponentialRampToValueAtTime(50, now + 0.3);

        fallGain.gain.setValueAtTime(0.15, now);
        fallGain.gain.exponentialRampToValueAtTime(0.01, now + 0.3);

        fallOsc.start(now);
        fallOsc.stop(now + 0.3);

        // Cleanup
        noiseSource.onended = () => {
            noiseSource.disconnect();
            noiseFilter.disconnect();
            noiseGain.disconnect();
        };
        thudOsc.onended = () => {
            thudOsc.disconnect();
            thudGain.disconnect();
        };
        fallOsc.onended = () => {
            fallOsc.disconnect();
            fallGain.disconnect();
        };
    }

    playHeartSound(): void {
        const ctx = this.ctxManager.get();
        if (!ctx) return;

        this.ctxManager.resume();
        const now = ctx.currentTime;

        const heartMelody: MelodyNote[] = [
            { freq: 523.25, time: 0 },      // C5
            { freq: 659.25, time: 0.08 },   // E5
            { freq: 783.99, time: 0.16 }    // G5
        ];

        this.playMelody(ctx, heartMelody, now, 'sine', 0.15, 3000);
    }

    playStarSound(): void {
        const ctx = this.ctxManager.get();
        if (!ctx) return;

        this.ctxManager.resume();
        const now = ctx.currentTime;

        const starMelody: MelodyNote[] = [
            { freq: 659.25, time: 0 },      // E5
            { freq: 830.61, time: 0.06 },   // G#5
            { freq: 987.77, time: 0.12 },   // B5
            { freq: 1318.51, time: 0.18 }   // E6
        ];

        this.playMelody(ctx, starMelody, now, 'triangle', 0.2);
    }

    playItemSpawnSound(): void {
        const ctx = this.ctxManager.get();
        if (!ctx) return;

        this.ctxManager.resume();
        const now = ctx.currentTime;

        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        const filter = ctx.createBiquadFilter();

        filter.type = 'lowpass';
        filter.frequency.value = 1500;

        oscillator.connect(filter);
        filter.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.frequency.value = 587.33; // D5
        oscillator.type = 'sine';

        const duration = 0.1;

        gainNode.gain.setValueAtTime(0, now);
        gainNode.gain.linearRampToValueAtTime(0.08, now + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.001, now + duration);

        oscillator.start(now);
        oscillator.stop(now + duration);

        oscillator.onended = () => {
            oscillator.disconnect();
            filter.disconnect();
            gainNode.disconnect();
        };
    }

    /**
     * 진화 사운드 - 파워업 느낌의 상승 아르페지오
     */
    playEvolveSound(): void {
        const ctx = this.ctxManager.get();
        if (!ctx) return;

        this.ctxManager.resume();
        const now = ctx.currentTime;

        const evolveMelody: MelodyNote[] = [
            { freq: 523.25, time: 0 },       // C5
            { freq: 659.25, time: 0.05 },    // E5
            { freq: 783.99, time: 0.10 },    // G5
            { freq: 1046.50, time: 0.15 },   // C6
            { freq: 1318.51, time: 0.20 }    // E6
        ];

        this.playMelody(ctx, evolveMelody, now, 'triangle', 0.15, 4000);

        // 샤인 효과음
        const shineOsc = ctx.createOscillator();
        const shineGain = ctx.createGain();
        shineOsc.connect(shineGain);
        shineGain.connect(ctx.destination);

        shineOsc.type = 'sine';
        shineOsc.frequency.setValueAtTime(2000, now + 0.25);
        shineOsc.frequency.exponentialRampToValueAtTime(4000, now + 0.4);

        shineGain.gain.setValueAtTime(0, now + 0.25);
        shineGain.gain.linearRampToValueAtTime(0.1, now + 0.3);
        shineGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);

        shineOsc.start(now + 0.25);
        shineOsc.stop(now + 0.5);
    }

    /**
     * 퇴화 사운드 - 하강 멜로디
     */
    playDevolveSound(): void {
        const ctx = this.ctxManager.get();
        if (!ctx) return;

        this.ctxManager.resume();
        const now = ctx.currentTime;

        const devolveMelody: MelodyNote[] = [
            { freq: 523.25, time: 0 },       // C5
            { freq: 392.00, time: 0.08 },    // G4
            { freq: 329.63, time: 0.16 },    // E4
            { freq: 261.63, time: 0.24 }     // C4
        ];

        this.playMelody(ctx, devolveMelody, now, 'sawtooth', 0.2, 1500);
    }

    /**
     * Near Miss 사운드 - 짧은 "휙" 소리
     */
    playNearMissSound(): void {
        const ctx = this.ctxManager.get();
        if (!ctx) return;

        this.ctxManager.resume();
        const now = ctx.currentTime;

        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        const filter = ctx.createBiquadFilter();

        filter.type = 'highpass';
        filter.frequency.value = 2000;

        oscillator.connect(filter);
        filter.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.type = 'sawtooth';
        oscillator.frequency.setValueAtTime(1500, now);
        oscillator.frequency.exponentialRampToValueAtTime(3000, now + 0.08);

        gainNode.gain.setValueAtTime(0.08, now);
        gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.1);

        oscillator.start(now);
        oscillator.stop(now + 0.1);

        oscillator.onended = () => {
            oscillator.disconnect();
            filter.disconnect();
            gainNode.disconnect();
        };
    }

    /**
     * 콤보 사운드 - 콤보에 따라 피치 상승
     */
    playComboSound(combo: number): void {
        const ctx = this.ctxManager.get();
        if (!ctx) return;

        this.ctxManager.resume();
        const now = ctx.currentTime;

        // 콤보가 높을수록 피치 상승
        const baseFreq = 600 + Math.min(combo, 20) * 50;

        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(baseFreq, now);
        oscillator.frequency.exponentialRampToValueAtTime(baseFreq * 1.5, now + 0.1);

        gainNode.gain.setValueAtTime(0.1, now);
        gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.15);

        oscillator.start(now);
        oscillator.stop(now + 0.15);

        oscillator.onended = () => {
            oscillator.disconnect();
            gainNode.disconnect();
        };
    }

    /**
     * 보스 경고 사운드 - 저주파 사이렌
     */
    playBossWarning(): void {
        const ctx = this.ctxManager.get();
        if (!ctx) return;

        this.ctxManager.resume();
        const now = ctx.currentTime;

        // 사이렌 효과 (2회 반복)
        for (let i = 0; i < 2; i++) {
            const startTime = now + i * 0.5;

            const oscillator = ctx.createOscillator();
            const gainNode = ctx.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(ctx.destination);

            oscillator.type = 'sawtooth';
            oscillator.frequency.setValueAtTime(200, startTime);
            oscillator.frequency.linearRampToValueAtTime(400, startTime + 0.25);
            oscillator.frequency.linearRampToValueAtTime(200, startTime + 0.5);

            gainNode.gain.setValueAtTime(0.15, startTime);
            gainNode.gain.setValueAtTime(0.15, startTime + 0.4);
            gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + 0.5);

            oscillator.start(startTime);
            oscillator.stop(startTime + 0.5);

            oscillator.onended = () => {
                oscillator.disconnect();
                gainNode.disconnect();
            };
        }
    }

    /**
     * 파워업 수집 사운드 (번개, 시계, 자석)
     */
    playPowerUpSound(): void {
        const ctx = this.ctxManager.get();
        if (!ctx) return;

        this.ctxManager.resume();
        const now = ctx.currentTime;

        const powerUpMelody: MelodyNote[] = [
            { freq: 698.46, time: 0 },       // F5
            { freq: 880.00, time: 0.06 },    // A5
            { freq: 1046.50, time: 0.12 },   // C6
            { freq: 1396.91, time: 0.18 }    // F6
        ];

        this.playMelody(ctx, powerUpMelody, now, 'square', 0.12, 2500);
    }

    /**
     * 보스 파괴 사운드 - 연속 폭발 (펑펑펑펑)
     */
    playBossDefeatSound(): void {
        const ctx = this.ctxManager.get();
        if (!ctx) return;

        this.ctxManager.resume();
        const now = ctx.currentTime;

        // 연속 폭발 (2번) - 펑펑
        const explosionTimes = [0, 0.1];
        const basePitch = 150;

        explosionTimes.forEach((delay, i) => {
            const startTime = now + delay;
            const volume = 0.4 + (i * 0.1); // 점점 커지는 볼륨

            // 노이즈 버스트
            const noiseLength = 0.15;
            const noiseBuffer = ctx.createBuffer(1, noiseLength * ctx.sampleRate, ctx.sampleRate);
            const noiseData = noiseBuffer.getChannelData(0);
            for (let j = 0; j < noiseData.length; j++) {
                noiseData[j] = (Math.random() * 2 - 1) * Math.pow(1 - j / noiseData.length, 1.5);
            }

            const noiseSource = ctx.createBufferSource();
            noiseSource.buffer = noiseBuffer;

            const noiseFilter = ctx.createBiquadFilter();
            noiseFilter.type = 'lowpass';
            noiseFilter.frequency.setValueAtTime(3000 + i * 500, startTime);
            noiseFilter.frequency.exponentialRampToValueAtTime(200, startTime + noiseLength);

            const noiseGain = ctx.createGain();
            noiseGain.gain.setValueAtTime(volume * 0.8, startTime);
            noiseGain.gain.exponentialRampToValueAtTime(0.01, startTime + noiseLength);

            noiseSource.connect(noiseFilter);
            noiseFilter.connect(noiseGain);
            noiseGain.connect(ctx.destination);
            noiseSource.start(startTime);

            // 저음 쿵
            const boomOsc = ctx.createOscillator();
            const boomGain = ctx.createGain();

            boomOsc.connect(boomGain);
            boomGain.connect(ctx.destination);

            boomOsc.type = 'sine';
            boomOsc.frequency.setValueAtTime(basePitch, startTime);
            boomOsc.frequency.exponentialRampToValueAtTime(20, startTime + 0.2);

            boomGain.gain.setValueAtTime(volume, startTime);
            boomGain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.25);

            boomOsc.start(startTime);
            boomOsc.stop(startTime + 0.25);

            // Cleanup
            noiseSource.onended = () => {
                noiseSource.disconnect();
                noiseFilter.disconnect();
                noiseGain.disconnect();
            };
            boomOsc.onended = () => {
                boomOsc.disconnect();
                boomGain.disconnect();
            };
        });

        // 마지막 대형 폭발
        const finalTime = now + 0.2;
        const finalNoiseLength = 0.4;
        const finalNoiseBuffer = ctx.createBuffer(1, finalNoiseLength * ctx.sampleRate, ctx.sampleRate);
        const finalNoiseData = finalNoiseBuffer.getChannelData(0);
        for (let i = 0; i < finalNoiseData.length; i++) {
            finalNoiseData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / finalNoiseData.length, 1.0);
        }

        const finalNoiseSource = ctx.createBufferSource();
        finalNoiseSource.buffer = finalNoiseBuffer;

        const finalFilter = ctx.createBiquadFilter();
        finalFilter.type = 'lowpass';
        finalFilter.frequency.setValueAtTime(5000, finalTime);
        finalFilter.frequency.exponentialRampToValueAtTime(100, finalTime + finalNoiseLength);

        const finalNoiseGain = ctx.createGain();
        finalNoiseGain.gain.setValueAtTime(0.7, finalTime);
        finalNoiseGain.gain.exponentialRampToValueAtTime(0.01, finalTime + finalNoiseLength);

        finalNoiseSource.connect(finalFilter);
        finalFilter.connect(finalNoiseGain);
        finalNoiseGain.connect(ctx.destination);
        finalNoiseSource.start(finalTime);

        // 마지막 저음 쿵 (가장 깊게)
        const finalBoom = ctx.createOscillator();
        const finalBoomGain = ctx.createGain();

        finalBoom.connect(finalBoomGain);
        finalBoomGain.connect(ctx.destination);

        finalBoom.type = 'sine';
        finalBoom.frequency.setValueAtTime(120, finalTime);
        finalBoom.frequency.exponentialRampToValueAtTime(30, finalTime + 0.5);

        finalBoomGain.gain.setValueAtTime(0.6, finalTime);
        finalBoomGain.gain.exponentialRampToValueAtTime(0.01, finalTime + 0.5);

        finalBoom.start(finalTime);
        finalBoom.stop(finalTime + 0.5);

        finalNoiseSource.onended = () => {
            finalNoiseSource.disconnect();
            finalFilter.disconnect();
            finalNoiseGain.disconnect();
        };
        finalBoom.onended = () => {
            finalBoom.disconnect();
            finalBoomGain.disconnect();
        };
    }

    /**
     * 폭탄 폭발 사운드
     */
    playBombSound(): void {
        const ctx = this.ctxManager.get();
        if (!ctx) return;

        this.ctxManager.resume();
        const now = ctx.currentTime;

        // 노이즈 폭발
        const noiseLength = 0.3;
        const noiseBuffer = ctx.createBuffer(1, noiseLength * ctx.sampleRate, ctx.sampleRate);
        const noiseData = noiseBuffer.getChannelData(0);
        for (let i = 0; i < noiseData.length; i++) {
            noiseData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / noiseData.length, 1.5);
        }

        const noiseSource = ctx.createBufferSource();
        noiseSource.buffer = noiseBuffer;

        const noiseFilter = ctx.createBiquadFilter();
        noiseFilter.type = 'lowpass';
        noiseFilter.frequency.setValueAtTime(3000, now);
        noiseFilter.frequency.exponentialRampToValueAtTime(500, now + noiseLength);

        const noiseGain = ctx.createGain();
        noiseGain.gain.setValueAtTime(0.5, now);
        noiseGain.gain.exponentialRampToValueAtTime(0.01, now + noiseLength);

        noiseSource.connect(noiseFilter);
        noiseFilter.connect(noiseGain);
        noiseGain.connect(ctx.destination);
        noiseSource.start(now);

        // 저음 쿵
        const boomOsc = ctx.createOscillator();
        const boomGain = ctx.createGain();

        boomOsc.connect(boomGain);
        boomGain.connect(ctx.destination);

        boomOsc.type = 'sine';
        boomOsc.frequency.setValueAtTime(100, now);
        boomOsc.frequency.exponentialRampToValueAtTime(20, now + 0.4);

        boomGain.gain.setValueAtTime(0.4, now);
        boomGain.gain.exponentialRampToValueAtTime(0.01, now + 0.4);

        boomOsc.start(now);
        boomOsc.stop(now + 0.4);

        noiseSource.onended = () => {
            noiseSource.disconnect();
            noiseFilter.disconnect();
            noiseGain.disconnect();
        };
        boomOsc.onended = () => {
            boomOsc.disconnect();
            boomGain.disconnect();
        };
    }

    playMelody(
        ctx: AudioContext,
        notes: MelodyNote[],
        now: number,
        waveType: OscillatorType,
        duration: number,
        filterFreq: number | null = null
    ): void {
        notes.forEach(note => {
            const oscillator = ctx.createOscillator();
            const gainNode = ctx.createGain();

            if (filterFreq !== null) {
                const filter = ctx.createBiquadFilter();
                filter.type = 'lowpass';
                filter.frequency.value = filterFreq;
                oscillator.connect(filter);
                filter.connect(gainNode);
            } else {
                oscillator.connect(gainNode);
            }
            gainNode.connect(ctx.destination);

            oscillator.frequency.value = note.freq;
            oscillator.type = waveType;

            const startTime = now + note.time;
            const volume = waveType === 'sine' ? 0.15 : 0.2;

            gainNode.gain.setValueAtTime(0, startTime);
            gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.01);
            gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

            oscillator.start(startTime);
            oscillator.stop(startTime + duration);

            oscillator.onended = () => {
                oscillator.disconnect();
                gainNode.disconnect();
            };
        });
    }
}
